import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { SessionService } from 'src/app/services/session.service';
import { EmdDateRange, EmdTab, EmdFilterService } from './emd-filter.service';
import { EmdChip, EmdTrendBucket } from './emd-mock-data';
import { EmdSelectionService } from './emd-selection.service';

/**
 * A usable CSS `<color>` from whatever `SessionService.getColorCodes()` returned.
 *
 * That method returns one of two shapes: a one-element **array** (`['#7b2ff7']`)
 * or the **string** `"'linear-gradient(to right,#a,#b)'"` — with literal single
 * quotes. `pages.component` feeds it to `[ngStyle].background` on the top rule,
 * where an invalid value is simply dropped. A CSS custom property is not so
 * forgiving: it accepts *any* token, so setting `--emd-accent` to that string made
 * every `var(--emd-accent, #1363df)` consumer invalid at computed-value time —
 * and the fallback is skipped, because the variable IS defined. The chip count
 * badges and the filter panel's tab lost their background entirely (a white icon
 * on a now-transparent tab reads as a missing icon).
 *
 * So: unwrap the quotes, take the first colour stop out of a gradient, and return
 * undefined when there is nothing usable — the components then get no variable at
 * all and their `#1363df` fallback applies.
 */
function accentColorFrom(value: unknown): string | undefined {
  const raw = String(value ?? '').replace(/^'(.*)'$/, '$1').trim();
  if (!raw) { return undefined; }
  const hex = raw.match(/#(?:[0-9a-f]{8}|[0-9a-f]{6}|[0-9a-f]{3,4})/i);
  if (hex) { return hex[0]; }
  const rgb = raw.match(/rgba?\([^)]*\)/i);
  if (rgb) { return rgb[0]; }
  return /gradient/i.test(raw) ? undefined : raw;
}

/**
 * EMD (Wholesaler) dashboard shell: page chrome, filter bar, tab strip and the
 * per-tab range controls. Each tab's content lives in its own component.
 */
@Component({
    selector: 'app-emd-dashboard',
    templateUrl: './emd-dashboard.component.html',
    styleUrls: ['./emd-dashboard.component.scss'],
    // Scoped here, not in root: the cross-filter selection and the badge filters
    // are shared by every visual on this dashboard and survive tab switches, but
    // reset on leaving.
    providers: [EmdSelectionService, EmdFilterService],
    standalone: false
})
export class EmdDashboardComponent implements OnInit, OnDestroy {
  reqSubcription: Subscription[] = [];

  // Session-derived (matches pattern used in DashboardComponent & PagesComponent)
  clientLogo: any;
  /** Brand colour/gradient for the top rule, as `pages.component` binds it. */
  newColor: any;
  /** Single brand `<color>` for the --emd-accent var — see accentColorFrom. */
  accentColor?: string;

  tabs: EmdTab[] = ['Summary', 'Details', 'Inventory'];
  activeTab: EmdTab = 'Summary';

  /** Right slide-in filter panel open state (bar funnel + panel handle both toggle it). */
  filterPanelOpen = false;

  timeBuckets = ['W', 'M', 'Q', 'Y'];
  detailsBucket: EmdTrendBucket = 'M';

  /** Cast the pill's string emission back to the bucket union. */
  onDetailsBucketChange(value: string): void {
    this.detailsBucket = value as EmdTrendBucket;
  }

  dohModes = ['DOH', 'WOH'];
  inventoryDohMode = 'DOH';
  dayModes = ['D', 'W'];
  inventoryDayMode = 'D';

  constructor(
    private sessionService: SessionService,
    private filters: EmdFilterService,
    private selection: EmdSelectionService
  ) {}

  ngOnInit(): void {
    this.clientLogo = this.sessionService.getClintLogo();
    const colorCodes = this.sessionService.getColorCodes();
    // Unwrapped, unlike pages.component: the service wraps its gradient in
    // literal single quotes, which makes the whole `background` declaration
    // invalid, so the top rule silently loses the brand gradient.
    this.newColor = String(colorCodes ?? '').replace(/^'(.*)'$/, '$1');
    this.accentColor = accentColorFrom(colorCodes);
  }

  ngOnDestroy(): void {
    this.reqSubcription.forEach((s) => s.unsubscribe());
  }

  get activeChips(): EmdChip[] {
    return this.filters.chips(this.activeTab);
  }

  get activeDateRange(): EmdDateRange {
    return this.filters.dateRange(this.activeTab);
  }

  /** Time Range badge writes an inclusive date cut on the active tab. */
  onDateRangeChange(range: EmdDateRange): void {
    this.filters.setDateRange(this.activeTab, range);
  }

  onTabChange(tab: string): void {
    this.activeTab = tab as EmdTab;
  }

  /**
   * The filter bar writes the new selection onto the chip itself, so this only
   * has to tell the service to re-derive; the active tab picks the change up
   * through EmdFilterService.changes.
   */
  onChipChange(): void {
    this.filters.apply(this.activeTab);
  }

  onResetFilters(): void {
    this.selection.cancel();
    this.filters.reset(this.activeTab);
  }

  // Confirm/cancel intent no longer routes through the shell: each widget's
  // header hosts its own selection toolbar, and EmdSelectionService.confirm()
  // owns writing the staged values into the badge filters.
}
